import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Download, RefreshCw, Lock, Layers, Zap, TerminalSquare } from "lucide-react";
import { COUNTRIES, REGIONS, POOLS, POOL_HIDDEN, poolLabel, SESSION_TYPES, QTY_PRESETS, GEN_CAP, buildProxyLine, parseRemaining } from "./data";

const FIELD = "h-11 bg-white border-[#E4EEEC] focus:border-neon rounded-xl";

function GeneratorControls({
  accounts, hasAccounts, accId, setAccId, region, setRegion, country, setCountry, countries,
  poolList, pool, setPool, proxyType, setProxyType, sessionType, setSessionType, qty, setQty, onGenerate,
}) {
  return (
    <>
      <div>
        <Label className="text-zinc-500 text-sm">Plan</Label>
        <Select value={accId} onValueChange={setAccId} disabled={!hasAccounts}>
          <SelectTrigger data-testid="gen-account-select" className={`${FIELD} mt-1.5`}><div className="flex items-center gap-2"><Layers className="w-4 h-4 text-neon" aria-hidden="true" /><SelectValue placeholder="Purchase data first" /></div></SelectTrigger>
          <SelectContent>{accounts.map((a) => {
            const gb = parseRemaining(a.remote);
            return <SelectItem key={a.account_id} value={a.account_id}>Premium Residential{gb != null ? ` — ${gb} GB left` : ""}</SelectItem>;
          })}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-zinc-500 text-sm">Region</Label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {REGIONS.map(([r, flag, name]) => (
            <button key={r} onClick={() => setRegion(r)} data-testid={`region-${r}`}
              className={`h-16 rounded-2xl border flex flex-col items-center justify-center gap-0.5 transition-colors ${region === r ? "border-neon bg-[#E7F7F4]" : "border-[#E4EEEC] bg-white hover:border-neon/50"}`}>
              <span className="text-xl leading-none">{flag}</span><span className="text-[10px] text-zinc-500">{name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-zinc-500 text-sm">Proxy type</Label>
          <button onClick={() => setProxyType(proxyType === "sticky" ? "rotating" : "sticky")} data-testid="proxy-type-toggle" className="text-xs font-medium text-neon hover:underline">
            Change to {proxyType === "sticky" ? "Rotating" : "Sticky"}
          </button>
        </div>
        <div className="mt-1.5 rounded-xl border border-neon bg-[#E7F7F4] px-4 py-3" data-testid="proxy-type-card">
          <div className="flex items-center gap-2 font-semibold text-ink">
            {proxyType === "sticky" ? <Lock className="w-4 h-4 text-neon" aria-hidden="true" /> : <RefreshCw className="w-4 h-4 text-neon" aria-hidden="true" />}
            {proxyType === "sticky" ? "Sticky" : "Rotating"}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">{proxyType === "sticky" ? "Keeps the same IP for the duration of the session." : "New IP on every request."}</p>
        </div>
      </div>
      {proxyType === "sticky" && (
        <div>
          <Label className="text-zinc-500 text-sm">Session type</Label>
          <div className="mt-1.5 grid grid-cols-3 gap-1 bg-[#F4F7F6] border border-[#E4EEEC] rounded-xl p-1">
            {SESSION_TYPES.map(([key, label]) => (
              <button key={key} onClick={() => setSessionType(key)} data-testid={`session-${key}`}
                className={`h-10 rounded-lg flex items-center justify-center text-sm font-semibold tracking-wide transition-colors ${sessionType === key ? "bg-neon text-white shadow" : "text-zinc-600 hover:text-ink"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-zinc-500" data-testid="session-type-desc">{SESSION_TYPES.find(([k]) => k === sessionType)?.[2]}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-500 text-sm">Country</Label>
          <Select value={country} onValueChange={setCountry}><SelectTrigger data-testid="gen-country-select" className={`${FIELD} mt-1.5`}><SelectValue /></SelectTrigger>
            <SelectContent>{countries.map(([c, name]) => <SelectItem key={c} value={c}>{name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-zinc-500 text-sm">Pool</Label>
          <Select value={pool} onValueChange={setPool}><SelectTrigger data-testid="gen-pool-select" className={`${FIELD} mt-1.5`}><SelectValue /></SelectTrigger>
            <SelectContent>{poolList.map((p) => <SelectItem key={p} value={p}>{poolLabel(p)}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between"><Label className="text-zinc-500 text-sm">Quantity <span className="text-zinc-400">(max 10k)</span></Label><span className="text-xs text-zinc-400 font-mono">Current: {qty}</span></div>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {QTY_PRESETS.map((p) => <button key={p} onClick={() => setQty(p)} data-testid={`qty-${p}`} className={`h-10 px-3.5 rounded-xl border text-sm font-medium transition-colors ${qty === p ? "border-neon bg-[#E7F7F4] text-ink" : "border-[#E4EEEC] bg-white text-zinc-600 hover:border-neon/50"}`}>{p >= 1000 ? `${p / 1000}K` : p}</button>)}
          <Input type="number" min={1} max={GEN_CAP} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(GEN_CAP, Number(e.target.value) || 1)))} data-testid="qty-input" className="w-20 h-10 bg-white border-[#E4EEEC] focus:border-neon rounded-xl font-mono text-ink" />
        </div>
      </div>
      <Button onClick={onGenerate} disabled={!hasAccounts} data-testid="generate-proxies-button" className="w-full h-14 bg-neon text-white hover:bg-neon-hover font-semibold rounded-xl text-base disabled:opacity-60"><Zap className="w-4 h-4 mr-2" aria-hidden="true" /> {hasAccounts ? `Generate ${qty.toLocaleString()} Proxies` : "Purchase data to generate"}</Button>
    </>
  );
}

function GeneratorTerminal({ proxyType, sessionType, geo, poolToken, qty, output, onCopy, onDownload }) {
  const outLines = output ? output.split("\n") : [];
  return (
    <div className="rounded-2xl overflow-hidden border border-[#0d2b26] h-[520px] flex flex-col" style={{ background: "#061512" }}>
      <div className="flex items-center gap-2 px-4 h-11 border-b border-white/10 shrink-0">
        <span className="w-3 h-3 rounded-full bg-[#FF5F57]" /><span className="w-3 h-3 rounded-full bg-[#FEBC2E]" /><span className="w-3 h-3 rounded-full bg-[#28C840]" />
        <span className="ml-2 flex items-center gap-2 text-[#5EEAD4] font-mono text-xs"><TerminalSquare className="w-3.5 h-3.5" aria-hidden="true" /> frproxies@console</span>
        {output && <div className="ml-auto flex gap-2">
          <button onClick={onCopy} data-testid="copy-all-button" className="h-7 px-2.5 rounded-md bg-white/10 hover:bg-white/20 text-[#8ff5e5] text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          <button onClick={onDownload} data-testid="download-button" className="h-7 px-2.5 rounded-md bg-[#10B5A3] hover:bg-[#0E9E8E] text-white text-xs flex items-center gap-1"><Download className="w-3 h-3" /> .txt</button>
        </div>}
      </div>
      <div className="flex-1 overflow-auto p-4 font-mono text-xs leading-5">
        <div className="text-[#3f7a70]">$ proxynode generate --type {proxyType}{proxyType === "sticky" ? ` --session ${sessionType}` : ""} --geo {geo} --pool {poolToken} --count {qty}</div>
        {output ? (
          <>
            <div className="text-[#3f7a70] mb-1">✓ {outLines.length.toLocaleString()} endpoints ready</div>
            <pre data-testid="proxy-output" className="text-[#7DF5E4] whitespace-pre">{output}</pre>
          </>
        ) : (
          <div className="text-[#3f7a70] mt-2">// configure settings on the left, then hit generate<span className="inline-block w-2 h-4 bg-[#5EEAD4] ml-1 animate-pulse align-middle" /></div>
        )}
      </div>
    </div>
  );
}

export function Generator({ accounts, config, goShop, pools }) {
  const [accId, setAccId] = useState("");
  const [region, setRegion] = useState("us");
  const [country, setCountry] = useState("us");
  const [pool, setPool] = useState("general");
  const [proxyType, setProxyType] = useState("sticky");
  const [sessionType, setSessionType] = useState("hard");
  const [qty, setQty] = useState(100);
  const [output, setOutput] = useState("");

  const poolList = ((pools && pools.length) ? pools : POOLS).filter((p) => !POOL_HIDDEN.includes(p));
  const hasAccounts = accounts.length > 0;
  useEffect(() => { if (!accId && accounts[0]) setAccId(accounts[0].account_id); }, [accounts, accId, setAccId]);
  const countries = COUNTRIES[region] || COUNTRIES.us;
  useEffect(() => { if (!countries.find(([c]) => c === country)) setCountry(countries[0][0]); }, [region, countries, country, setCountry]);
  useEffect(() => { if (!poolList.includes(pool) && poolList[0]) setPool(poolList[0]); }, [poolList, pool]);

  const acc = accounts.find((a) => a.account_id === accId);
  const geo = country.toUpperCase();
  const poolToken = pool.toLowerCase();

  const generate = () => {
    if (!acc) { toast.error("Create a proxy account first"); goShop(); return; }
    const n = Math.min(Math.max(1, qty), GEN_CAP);
    const host = config?.host || "residential.wealthproxies.com", port = config?.port || "3128";
    const lines = [];
    for (let i = 0; i < n; i++) {
      lines.push(buildProxyLine({ host, port, username: acc.username, password: acc.password, proxyType, sessionType, poolToken, geo }));
    }
    setOutput(lines.join("\n"));
    toast.success(`Generated ${n.toLocaleString()} ${proxyType === "rotating" ? "rotating" : sessionType} proxies`);
  };
  const copyAll = () => { if (output) { navigator.clipboard.writeText(output); toast.success("Copied all proxies"); } };
  const download = () => {
    if (!output) return;
    const url = URL.createObjectURL(new Blob([output], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url; a.download = `proxies-${proxyType === "rotating" ? "rotating" : sessionType}-${qty}.txt`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-4">
        <GeneratorControls
          accounts={accounts} hasAccounts={hasAccounts} accId={accId} setAccId={setAccId}
          region={region} setRegion={setRegion} country={country} setCountry={setCountry} countries={countries}
          poolList={poolList} pool={pool} setPool={setPool} proxyType={proxyType} setProxyType={setProxyType}
          sessionType={sessionType} setSessionType={setSessionType} qty={qty} setQty={setQty} onGenerate={generate} />
      </div>
      <div className="lg:col-span-7">
        <GeneratorTerminal proxyType={proxyType} sessionType={sessionType} geo={geo} poolToken={poolToken}
          qty={qty} output={output} onCopy={copyAll} onDownload={download} />
      </div>
    </div>
  );
}
